import { Dtos, SuretyAccountId, TagId } from "@inrev/common";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useRequest } from "../../../utils/request";
import { bondPreviewsQueryKey } from "../../bond/bond.keys";
import { bondRequestPreviewsQueryKey } from "../../bond-request/bond-request.keys";
import { accountPreviewsQueryKey } from "../../previews/previews.keys";
import { adminEntityTagsQueryKey } from "../entity.keys";
import { EntityId, EntityType } from "./types";

export const useAdminRemoveTagFromEntity = () => {
	const queryClient = useQueryClient();
	const { _delete } = useRequest();

	const mutation = useMutation({
		mutationFn: async ({
			tagId,
			entityType,
			entityId,
			accountId,
		}: {
			tagId: TagId;
			entityType: EntityType;
			entityId: EntityId;
			accountId?: SuretyAccountId;
		}) => {
			const base = accountId
				? `/v2/admin/surety/accounts/${accountId}/${entityType}s/${entityId}`
				: `/v2/admin/surety/${entityType}s/${entityId}`;
			await _delete(`${base}/tags/${tagId}`, undefined, "none");
		},
		onMutate: async ({ tagId, entityId, entityType }) => {
			await queryClient.cancelQueries({
				queryKey: adminEntityTagsQueryKey(entityType, entityId),
			});

			const previousTags = queryClient.getQueryData<Dtos.EntityTag.GetByEntity.Response>(
				adminEntityTagsQueryKey(entityType, entityId),
			);

			queryClient.setQueryData<Dtos.EntityTag.GetByEntity.Response>(
				adminEntityTagsQueryKey(entityType, entityId),
				(old) => {
					return old?.filter((tag) => tag.id !== tagId) ?? [];
				},
			);

			return { previousTags };
		},
		onError: (_err, { entityId, entityType }, context) => {
			queryClient.setQueryData(
				adminEntityTagsQueryKey(entityType, entityId),
				context?.previousTags,
			);
		},
		onSuccess: (_data, { entityId, entityType }) => {
			queryClient.invalidateQueries({
				queryKey: adminEntityTagsQueryKey(entityType, entityId),
			});
			switch (entityType) {
				case "account":
					queryClient.invalidateQueries({ queryKey: accountPreviewsQueryKey() });
					break;
				case "quote":
					queryClient.invalidateQueries({ queryKey: bondRequestPreviewsQueryKey() });
					break;
				case "bond":
					queryClient.invalidateQueries({ queryKey: bondPreviewsQueryKey() });
					break;
			}
		},
	});

	return {
		removeTagFromEntity: mutation.mutate,
		removeTagFromEntityLoading: mutation.isPending,
		removeTagFromEntityError: mutation.error,
		dismissRemoveTagFromEntityError: () => {
			mutation.reset();
		},
	};
};
